import { useState } from "react";
import { Pressable } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { Banner, Button, Checkbox, Input } from "@visioni/a3s-ui/components";
import {
  PATRIOT_BACKFILL_LANES,
  UNDATED_PATRIOT_BACKFILL_LANES,
  useBackfillRuns,
  useStartPatriotBackfill,
  type PatriotBackfillLane,
  type PatriotBackfillRun,
  type StartPatriotBackfillResponse,
} from "@visioni/a3s-monitoring/app";
import { Text, View } from "@src/components";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { PATRIOT_BACKFILL_LANE_LABELS, backfillRequest, isUndatedOnly } from "../backfill-request";
import { CursorPagerBar } from "../components/CursorPagerBar";
import { ActivationDateRangeField, type DayRangeValues } from "../components/ActivationDateRangeField";
import { PatriotToolsPage } from "../components/PatriotToolsPage";
import { QueryNotice } from "../components/QueryNotice";

/** How long the history keeps refreshing after a start, waiting for the run's record to appear. */
const AWAIT_RECORD_MS = 2 * 60_000;

const DATED_LANES = PATRIOT_BACKFILL_LANES.filter((lane) => !UNDATED_PATRIOT_BACKFILL_LANES.includes(lane));

export function BackfillScreen() {
  const [pollUntil, setPollUntil] = useState<number | undefined>(undefined);

  return (
    <PatriotToolsPage
      title="Backfill"
      subtitle="Copies Patriot records into a3s again over a range of time, for the kinds of record you choose. Use it to fill a gap, or to refresh records that look out of date."
    >
      <FeatureGate
        flag={FEATURE_FLAGS.PATRIOT_TOOLS_BACKFILL}
        fallback={
          <Banner
            variant="info"
            title="Backfill isn't enabled for you"
            message="Ask engineering to turn on patriot_tools_backfill if you need it."
          />
        }
      >
        <View gap="3xl">
          <View style={styles.raised}>
            <BackfillForm onStarted={() => setPollUntil(Date.now() + AWAIT_RECORD_MS)} />
          </View>
          <BackfillHistory pollUntil={pollUntil} />
        </View>
      </FeatureGate>
    </PatriotToolsPage>
  );
}

function BackfillForm({ onStarted }: { onStarted: () => void }) {
  const backfill = useStartPatriotBackfill();
  const [lanes, setLanes] = useState<readonly PatriotBackfillLane[]>([]);
  const [days, setDays] = useState<DayRangeValues>({});
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");
  const [force, setForce] = useState(false);

  const { request, errors } = backfillRequest({ lanes, days, fromTime, toTime, force });

  const setLane = (lane: PatriotBackfillLane, checked: boolean) => {
    // Kept in the reconciler's lane order, whatever order they were set in.
    setLanes((current) => PATRIOT_BACKFILL_LANES.filter((l) => (l === lane ? checked : current.includes(l))));
  };
  const start = () => {
    if (!request) return;
    backfill.mutate(request, {
      onSuccess: (started) => {
        showSuccessToast({ message: "Backfill started", description: startedDescription(started) });
        onStarted();
      },
      onError: (error) => showErrorToast({ error }),
    });
  };

  const laneRow = (lane: PatriotBackfillLane) => (
    <CheckRow
      key={lane}
      label={PATRIOT_BACKFILL_LANE_LABELS[lane]}
      checked={lanes.includes(lane)}
      onChange={(checked) => setLane(lane, checked)}
    />
  );

  return (
    <View gap="xl">
      <View gap="md" style={styles.raised}>
        <Text textStyle="sm/semibold" color="text">
          Time-based records
        </Text>
        {DATED_LANES.map(laneRow)}

        <View gap="sm" style={styles.range}>
          <View style={styles.raised}>
            <ActivationDateRangeField value={days} onChange={setDays} placeholder="Choose the days to copy" />
          </View>
          <View flexDirection="row" gap="md">
            <View flex={1}>
              <Input
                label="From time"
                placeholder="00:00"
                value={fromTime}
                onChangeText={setFromTime}
                error={errors.fromTime}
                helperText="On the first day. Blank is midnight."
                size="small"
              />
            </View>
            <View flex={1}>
              <Input
                label="To time"
                placeholder="End of day"
                value={toTime}
                onChangeText={setToTime}
                error={errors.toTime}
                helperText="On the last day. Blank is the end of it."
                size="small"
              />
            </View>
          </View>
          {errors.range !== undefined && (
            <Text textStyle="xs/regular" style={styles.error}>
              {errors.range}
            </Text>
          )}
          <Text textStyle="xs/regular" color="textMuted">
            {`Times are 24-hour, in your timezone (${Intl.DateTimeFormat().resolvedOptions().timeZone}). A run stops after 15 minutes, so split a long range into several shorter ones.`}
          </Text>
        </View>
      </View>

      <View gap="md">
        <View gap="xs">
          <Text textStyle="sm/semibold" color="text">
            Records
          </Text>
          <Text textStyle="xs/regular" color="textMuted">
            Patriot has no date to filter these on, so a backfill copies all of them.
          </Text>
        </View>
        {UNDATED_PATRIOT_BACKFILL_LANES.map(laneRow)}
      </View>

      <CheckRow
        label="Copy every record again"
        detail="Including records a3s already has and that haven't changed. Slower; use it when stored records look wrong."
        checked={force}
        onChange={setForce}
      />

      <View alignItems="flex-start">
        <Button
          label="Start backfill"
          size="sm"
          onPress={start}
          disabled={!request}
          loading={backfill.isPending}
          loadingText="Starting…"
        />
      </View>
    </View>
  );
}

/** What a started backfill covers, and where it will show up. */
function startedDescription({ backfill }: StartPatriotBackfillResponse): string {
  const lanes = backfill.lanes.map((lane) => PATRIOT_BACKFILL_LANE_LABELS[lane]).join(", ");
  const force = backfill.force ? ", copying every record again" : "";
  return `${lanes}, ${rangeLabel(backfill)}${force}. It appears in the list below shortly.`;
}

function BackfillHistory({ pollUntil }: { pollUntil: number | undefined }) {
  const runs = useBackfillRuns({ pollUntil });

  return (
    <View gap="md">
      <Text textStyle="sm/semibold" color="text">
        Backfills, newest first
      </Text>
      {runs.items === undefined ?
        <QueryNotice
          isLoading={runs.isLoading}
          error={runs.error}
          onRetry={runs.refetch}
          loadingLabel="Loading backfills…"
          errorTitle="Could not load backfills"
        />
      : runs.items.length === 0 && !runs.pager.canGoBack ?
        <Text textStyle="sm/regular" color="textMuted">
          No backfills yet.
        </Text>
      : <View gap="md">
          <View style={styles.card}>
            {runs.items.map((run, index) => (
              <BackfillRunRow key={run.runId} run={run} first={index === 0} />
            ))}
          </View>
          <CursorPagerBar
            pager={runs.pager}
            nextCursor={runs.nextCursor}
            isFetching={runs.isFetching}
            summary={`${String(runs.items.length)} backfills`}
          />
        </View>
      }
    </View>
  );
}

function BackfillRunRow({ run, first }: { run: PatriotBackfillRun; first: boolean }) {
  return (
    <View gap="xs" style={[styles.row, first && styles.firstRow]}>
      <Text textStyle="xs/regular" color="textMuted">
        {`${formatMoment(run.startedAt)} · ${run.requestedByEmail ?? run.requestedBy ?? "Started outside Patriot tools"}`}
      </Text>
      <Text textStyle="sm/regular" color="text">
        {`${run.lanes.map((lane) => PATRIOT_BACKFILL_LANE_LABELS[lane]).join(", ")} · ${rangeLabel(run)}${run.force ? " · every record again" : ""}`}
      </Text>
    </View>
  );
}

/** The span a backfill covered, or that it took every record of undated lanes. */
function rangeLabel({ lanes, start, end }: Pick<PatriotBackfillRun, "lanes" | "start" | "end">): string {
  if (isUndatedOnly(lanes)) return "all records";
  return start !== undefined && end !== undefined ? `${formatMoment(start)} to ${formatMoment(end)}` : "no range";
}

const formatMoment = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

function CheckRow({
  label,
  detail,
  checked,
  onChange,
}: {
  label: string;
  detail?: string | undefined;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Pressable onPress={() => onChange(!checked)} accessibilityRole="checkbox" accessibilityState={{ checked }}>
      <View flexDirection="row" alignItems="flex-start" gap="md">
        {/* The row is the one press target: an interactive box inside it would handle the same press twice. */}
        <View pointerEvents="none">
          <Checkbox checked={checked} />
        </View>
        <View gap="xs" style={styles.checkText}>
          <Text textStyle="sm/regular" color="text">
            {label}
          </Text>
          {detail !== undefined && (
            <Text textStyle="xs/regular" color="textMuted">
              {detail}
            </Text>
          )}
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create((theme) => ({
  range: { maxWidth: 480 },
  // A dropdown opens over the siblings after it only when its ancestors stack above them.
  raised: { zIndex: 1 },
  checkText: { flex: 1 },
  error: { color: theme.colors.status.error },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  row: {
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  firstRow: { borderTopWidth: 0 },
}));
